import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, ChevronDown, HelpCircle, Heart } from 'lucide-react';

export default function FAQ() {
    const [openFaq, setOpenFaq] = useState(null);

    const faqs = [
        {
            question: "Is my health data secure?",
            answer: "Absolutely. We use bank-level encryption to protect your data. Your health information is never shared with third parties and you can delete your data at any time. We are HIPAA-aware in our design practices."
        },
        {
            question: "How can I delete my data?",
            answer: "You can delete your account and all associated data at any time from within the app. Simply go to Settings in the menu, scroll down to the 'Danger Zone' section, and click 'Delete Account'. This will permanently remove all your personal information, health tracking data, and any other data associated with your account from our servers."
        },
        {
            question: "Can I share my data with my doctor?",
            answer: "Yes! Premium users can export their complete health timeline as a PDF report, perfect for sharing with healthcare providers during appointments."
        },
        {
            question: "What autoimmune conditions does the app support?",
            answer: "Our app is designed to support all autoimmune conditions including Lupus, Rheumatoid Arthritis, Multiple Sclerosis, Hashimoto's, Crohn's, Psoriasis, and many more. The tracking tools are flexible enough to accommodate any condition."
        },
        {
            question: "How does the AI health assistant work?",
            answer: "Our AI is trained on autoimmune health literature and can answer questions, help identify patterns, and provide general guidance. It's not a replacement for medical advice but a helpful companion for your health journey."
        },
        {
            question: "Can I cancel my subscription anytime?",
            answer: "Yes, you can cancel your subscription at any time with no questions asked. Your access will continue until the end of your current billing period (month or year). You can also upgrade your plan at any time and the change will take effect immediately."
        },
        {
            question: "Is there a mobile app?",
            answer: "Yes! Autoimmune Pathways is a Progressive Web App (PWA), which means you can install it on your phone directly from your browser. It works offline and sends push notifications for medication reminders."
        },
        {
            question: "How do the AI correlations work?",
            answer: "Our AI analyzes your tracking data to find patterns between your food intake, sleep quality, weather conditions, menstrual cycle, and symptom flares. The more consistently you track, the more accurate these correlations become."
        },
        {
            question: "Can I track my lab results?",
            answer: "Yes! You can log lab results like CRP, ESR, ANA, thyroid panels, and more. These values appear in your medical timeline and can be graphed alongside your symptoms to help you and your doctor identify trends."
        },
        {
            question: "What is the AIP food tracker?",
            answer: "The Autoimmune Protocol (AIP) food tracker helps you identify which foods may be triggering your symptoms. You can log meals, mark foods as AIP-compliant or not, and track any reactions you experience."
        },
        {
            question: "How do I get started?",
            answer: "Simply create an account and choose a plan. You can start with our Essential tier to try the basic features, then upgrade to Premium or Professional for advanced AI insights, PDF exports, and more."
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-slate-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                                <Heart className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                                <span className="font-bold text-lg text-white">Autoimmune Pathways</span>
                                <span className="text-xs sm:text-sm text-slate-400">by Me and My Pandas</span>
                            </div>
                        </Link>
                        <Link to="/">
                            <Button variant="ghost" className="text-slate-300 hover:text-white gap-2">
                                <ArrowLeft className="w-4 h-4" />
                                Back to Home
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Content */}
            <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-500/20 mb-6">
                            <HelpCircle className="w-8 h-8 text-indigo-400" />
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                            Frequently Asked Questions
                        </h1>
                        <p className="text-lg text-slate-400">
                            Got questions? We've got answers. Find everything you need to know about Autoimmune Pathways.
                        </p>
                    </div>

                    {/* FAQ List */}
                    <Card className="bg-slate-900/50 border-slate-700">
                        <CardContent className="p-0">
                            <div className="divide-y divide-slate-700">
                                {faqs.map((faq, index) => (
                                    <div key={index}>
                                        <button
                                            onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                            className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-800/50 transition-colors"
                                        >
                                            <span className="font-medium text-white pr-4">{faq.question}</span>
                                            <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                                        </button>
                                        {openFaq === index && (
                                            <div className="px-5 pb-5 pt-0">
                                                <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Contact Section */}
                    <div className="mt-12 text-center">
                        <p className="text-slate-400 mb-4">
                            Still have questions? We're here to help.
                        </p>
                        <a 
                            href="mailto:contact@meandmypandas.com" 
                            className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors"
                        >
                            Contact us at contact@meandmypandas.com
                        </a>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-sm text-slate-500">
                        © {new Date().getFullYear()} Autoimmune Pathways. All rights reserved.
                    </p>
                    <div className="flex items-center gap-6 text-sm">
                        <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors">
                            Privacy Policy
                        </Link>
                        <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">
                            Terms of Service
                        </Link>
                        <Link to="/medical-disclaimer" className="text-slate-400 hover:text-white transition-colors">
                            Medical Disclaimer
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
